import TrustLayout, { Section, P, UL } from "../components/TrustLayout";
import { CheckCircle } from "lucide-react";

const versionHistory = [
  { version: "1.1", date: "28 July 2026", notes: "Added Mailchimp and Second Nature AI as sub-processors." },
  { version: "1.0", date: "2 April 2026", notes: "Initial publication of the Technofocus Sub-Processor list." },
];

const subProcessorSections = [
  { id: "sp-intro", label: "Introduction" },
  { id: "sp-categories", label: "Categories of Sub-Processing" },
  { id: "sp-list", label: "Current Sub-Processor List" },
  { id: "sp-transfers", label: "International Data Transfers" },
  { id: "sp-updates", label: "Updates to Sub-Processors" },
  { id: "sp-commitments", label: "Data Protection Commitments" },
  { id: "sp-contact", label: "Contact" },
];

const subProcessors = [
  { name: "Microsoft Azure", purpose: "Cloud infrastructure hosting", location: "United States", data: "User identity, platform data" },
  { name: "Cypher Learning", purpose: "Learning Management System (LMS)", location: "United States", data: "Course enrollment, progress" },
  { name: "Emergent", purpose: "Web application hosting platform", location: "United States", data: "User identity, role data, usage data" },
  { name: "Skillable", purpose: "Lab hosting platform", location: "United States", data: "User identity, lab activity" },
  { name: "GoDeploy", purpose: "Lab hosting platform", location: "United Kingdom", data: "User identity, lab activity" },
  { name: "Spektra Systems", purpose: "Lab hosting platform", location: "United States", data: "User identity, lab activity" },
  { name: "Navvatic", purpose: "Simulation labs platform", location: "United States", data: "User interaction data" },
  { name: "Supademo", purpose: "Simulation labs platform", location: "United States", data: "User interaction data" },
  { name: "SCORM Cloud", purpose: "SCORM content syndication", location: "United States", data: "Course access and usage data" },
  { name: "RSS.com", purpose: "Podcast hosting platform", location: "United States", data: "Limited user interaction data" },
  { name: "Synthesia", purpose: "Video generation and hosting", location: "United Kingdom / Global", data: "User interaction data" },
  { name: "Cvent", purpose: "Event hosting and registration", location: "United States", data: "Registration and attendance data" },
  { name: "Credly", purpose: "Digital badge issuance", location: "United States", data: "Name, email, achievement data" },
  { name: "Learnyst", purpose: "LMS platform (specific programs)", location: "India", data: "Course participation data" },
  { name: "Extreme Arts & Sciences (EAS)", purpose: "Skilling Hub integration", location: "United States", data: "Name, email, completion data" },
  { name: "Microsoft", purpose: "Program reporting and integration", location: "Global", data: "Name, email, course progress" },
  { name: "CookieYes", purpose: "Cookie consent management", location: "Global", data: "Cookie preferences, device/browser data" },
  { name: "Mailchimp (The Rocket Science Group LLC)", purpose: "Learner communications and email campaign delivery", location: "United States", data: "Name, email address, organization, enrollment/status information, communication preferences and email engagement data" },
  { name: "Second Nature AI", purpose: "AI-assisted sales and pitch practice simulations and evaluation", location: "Netherlands, United States and EU", data: "First name, last name, email address, team/manager ID where applicable, practice activity, audio recordings and optional video recordings" },
];

const categories = [
  "Cloud hosting and infrastructure",
  "Learning management systems (LMS)",
  "Lab environments and simulation platforms",
  "Content delivery and media hosting",
  "Event management and engagement platforms",
  "Certification and badging services",
  "Integration and reporting platforms",
  "Cookie and consent management",
];

export default function SubProcessors() {
  return (
    <TrustLayout
      title="Sub-Processors"
      lastUpdated="28 July 2026"
      versionHistory={versionHistory}
      sections={subProcessorSections}
      testId="subprocessors-page"
    >
      {/* Intro */}
      <Section id="sp-intro">
        <P>
          <strong>Technofocus Pte. Ltd.</strong> ("Technofocus") uses certain third-party service providers ("Sub-processors") to support the delivery and operation of its platforms, including LevelUp, Why Microsoft – Compete Enablement for Partners, Skilling-in-a-Box, and related services.
        </P>
        <P>
          These Sub-processors may process limited Personal Data strictly for the purpose of providing infrastructure, platform functionality, content delivery, analytics, and related services. Technofocus ensures that all Sub-processors are subject to appropriate contractual obligations, including confidentiality, data protection, and security requirements consistent with applicable data protection laws.
        </P>
      </Section>

      {/* Categories */}
      <Section id="sp-categories" title="Categories of Sub-Processing">
        <P>Technofocus engages Sub-processors across the following categories:</P>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          {categories.map(cat => (
            <div key={cat} className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
              <CheckCircle size={13} style={{ color: '#194449' }} className="shrink-0" />
              <span className="text-gray-700">{cat}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Sub-processor table */}
      <Section id="sp-list" title="Current Sub-Processor List">
        <P>The following table lists all current Sub-processors engaged by Technofocus:</P>
        <div className="overflow-x-auto mt-4 rounded-xl border" style={{ borderColor: '#E2EBE9' }}>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ backgroundColor: '#194449' }}>
                <th className="text-left px-4 py-3 text-white font-semibold text-xs uppercase tracking-wide">Sub-Processor</th>
                <th className="text-left px-4 py-3 text-white font-semibold text-xs uppercase tracking-wide">Purpose</th>
                <th className="text-left px-4 py-3 text-white font-semibold text-xs uppercase tracking-wide">Location</th>
                <th className="text-left px-4 py-3 text-white font-semibold text-xs uppercase tracking-wide">Data Processed</th>
              </tr>
            </thead>
            <tbody>
              {subProcessors.map((sp, i) => (
                <tr
                  key={sp.name}
                  className="border-t transition-colors hover:bg-gray-50"
                  style={{ borderColor: '#E2EBE9', backgroundColor: i % 2 === 0 ? '#FFFFFF' : '#FAFAFA' }}
                >
                  <td className="px-4 py-3 font-semibold text-gray-900 text-xs whitespace-nowrap">{sp.name}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{sp.purpose}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{sp.location}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{sp.data}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-400 mt-2 italic">
          Last updated: 28 July 2026 · {subProcessors.length} sub-processors listed
        </p>
      </Section>

      {/* Transfers */}
      <Section id="sp-transfers" title="International Data Transfers">
        <P>Due to the global nature of Technofocus services, Personal Data may be processed in jurisdictions outside the European Economic Area (EEA), including:</P>
        <UL items={["United States", "Singapore", "India", "United Kingdom"]} />
        <P>Technofocus ensures that all such transfers are subject to appropriate safeguards, including Standard Contractual Clauses (SCCs) and contractual data protection commitments with Sub-processors.</P>
      </Section>

      {/* Updates */}
      <Section id="sp-updates" title="Updates to Sub-Processors">
        <P>Technofocus may update this Sub-processor list from time to time to reflect changes in service providers or platform functionality. Where required by applicable agreements or law, Technofocus will take reasonable steps to notify customers of material changes.</P>
      </Section>

      {/* Commitments */}
      <Section id="sp-commitments" title="Data Protection Commitments">
        <P>Technofocus ensures that all Sub-processors:</P>
        <UL items={[
          "Process Personal Data only for specified purposes.",
          "Implement appropriate technical and organizational security measures.",
          "Maintain confidentiality obligations.",
          "Support compliance with applicable data protection laws.",
        ]} />
      </Section>

      {/* Contact */}
      <Section id="sp-contact" title="Contact">
        <P>For questions regarding Sub-processors or data protection practices, please contact:</P>
        <div className="rounded-xl border p-5 inline-block mt-1" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
          <p className="text-sm font-semibold text-gray-900 mb-1">Data Protection Enquiries</p>
          <a href="mailto:privacy@technofocus.ai" className="text-sm font-medium hover:underline" style={{ color: '#194449' }}>
            privacy@technofocus.ai
          </a>
        </div>
      </Section>

    </TrustLayout>
  );
}
